import { CodeEditorStatus, ModelErrorPattern } from './types';

export const MAIN_CLASS_NAME = 'code-editor';
export const READONLY_CLASS_NAME = `${MAIN_CLASS_NAME}_readonly`;

export const DEFAULT_EDITOR_STATUS = CodeEditorStatus.Default;

export const SCHEMA_FILE_NAME = 'schema.json';
export const JSON_FILE_NAME = 'content.json';

export const JSON_MARKERS_OWNER = 'json';

export const SCHEMA_I18N_KEYS = {
  TYPE: 'Schema: Must be of type {{type}}',
  MIN_LENGTH: 'Schema: Must be at least {{count}} characters long',
  MAX_LENGTH: 'Schema: Must not exceed {{count}} characters',
  PATTERN: 'Schema: Must match the required pattern',
  MINIMUM: 'Schema: Must be greater than or equal to {{value}}',
  MAXIMUM: 'Schema: Must be less than or equal to {{value}}',
  EXCLUSIVE_MINIMUM: 'Schema: Must be greater than {{value}}',
  EXCLUSIVE_MAXIMUM: 'Schema: Must be less than {{value}}',
  MULTIPLE_OF: 'Schema: Must be a multiple of {{value}}',
  MIN_ITEMS: 'Schema: Must contain at least {{count}} item',
  MIN_ITEMS_PLURAL: 'Schema: Must contain at least {{count}} items',
  MAX_ITEMS: 'Schema: Must not contain more than {{count}} item',
  MAX_ITEMS_PLURAL: 'Schema: Must not contain more than {{count}} items',
  UNIQUE_ITEMS: 'Schema: All items must be unique',
  MIN_PROPERTIES: 'Schema: Must have at least {{count}} property',
  MIN_PROPERTIES_PLURAL: 'Schema: Must have at least {{count}} properties',
  MAX_PROPERTIES: 'Schema: Must not have more than {{count}} property',
  MAX_PROPERTIES_PLURAL: 'Schema: Must not have more than {{count}} properties',
  REQUIRED: 'Schema: Missing required field',
  REQUIRED_PLURAL: 'Schema: Missing required fields',
  ENUM: 'Schema: Must be one of the allowed values',
  CONST: 'Schema: Must be equal to the specified constant value',
  TITLE_NON_NEGATIVE_INTEGER: 'Schema: Non-negative Integer',
  DESC_NON_NEGATIVE_INTEGER: 'Schema: An integer value that is greater than or equal to zero',
  DESC_ARRAY_WITH_ITEMS: 'Schema: An array containing one or more valid items',
  PROPERTY_NAME_CONTAINS_SPACE: 'Schema: Property name must not contain spaces',
} as const;

export const EDITOR_I18N_KEYS = {
  REQUIRED_PROPERTY: "Editor: Missing property '{{property}}'",
  ADDITIONAL_PROPERTY: "Editor: Property '{{property}}' is not allowed",
  TYPE_MISMATCH: "Editor: Expected type '{{expected}}' but got '{{actual}}'",
  ENUM_MISMATCH: "Editor: Value '{{value}}' is not one of the allowed options",
  CONST_MISMATCH: "Editor: Value '{{value}}' does not match the required constant",
  PATTERN_MISMATCH: "Editor: String does not match pattern '{{pattern}}'",
  MIN_LENGTH: 'Editor: String is too short (minimum {{min}} characters)',
  MAX_LENGTH: 'Editor: String is too long (maximum {{max}} characters)',
  MINIMUM: 'Editor: Value {{value}} is less than minimum {{min}}',
  MAXIMUM: 'Editor: Value {{value}} is greater than maximum {{max}}',
  MULTIPLE_OF: 'Editor: Value {{value}} must be a multiple of {{multipleOf}}',
  MIN_ITEMS: 'Editor: Array has too few items (minimum {{min}})',
  MAX_ITEMS: 'Editor: Array has too many items (maximum {{max}})',
  UNIQUE_ITEMS: 'Editor: Array items are not unique',
  ONE_OF: 'Editor: Value must match exactly one of the schemas',
  ANY_OF: 'Editor: Value must match at least one of the schemas',
  ALL_OF: 'Editor: Value must match all of the schemas',
  NOT_ALLOWED: "Editor: Value is not allowed by 'not' schema",
};

export const PARSER_I18N_KEYS = {
  VALUE_EXPECTED: 'Parser: Value expected',
  EXPECTED_JSON: 'Parser: Expected a JSON object, array or literal',
  DUPLICATE_KEY: "Parser: Duplicate object key '{{key}}'",
  UNEXPECTED_TOKEN: "Parser: Unexpected token '{{token}}'",
  EXPECTED_COLON: "Parser: Expected ':' after key",
  EXPECTED_COMMA_OR_BRACKET: "Parser: Expected ',' or closing bracket",
  TRAILING_COMMA_NOT_ALLOWED: 'Parser: Trailing comma not allowed',
  UNCLOSED_STRING: 'Parser: String not closed',
  EXPECTED_COMMA: 'Parser: Expected comma',
  PROPERTY_EXPECTED: 'Parser: Property expected',
  PROPERTY_KEY_QUOTES: 'Parser: Property keys must be double-quoted',
  EXPECTED_VALUE_AFTER_COLON: "Parser: Expected value after ':'",
  EXPECTED_OBJECT_KEY: 'Parser: Expected property name',
  EXPECTED_OBJECT_OR_ARRAY: 'Parser: Expected object or array',
  UNEXPECTED_END_OF_COMMENT: 'Parser: Unexpected end of comment',
  INVALID_ESCAPE_CHARACTER: 'Parser: Invalid escape character',
  INVALID_NUMBER_FORMAT: 'Parser: Invalid number format',
  UNEXPECTED_END_OF_NUMBER: 'Parser: Unexpected end of number',
  UNEXPECTED_END_OF_STRING: 'Parser: Unexpected end of string',
  INVALID_UNICODE_SEQUENCE: 'Parser: Invalid Unicode escape sequence',
  COMMENT_NOT_ALLOWED: 'Parser: Comments are not allowed in JSON',
  TRAILING_COMMA: 'Parser: Trailing comma',
  COLON_EXPECTED: 'Parser: Colon expected',
  DUPLICATE_PROPERTY: 'Parser: Duplicate property name',
  UNEXPECTED_END_OF_FILE: 'Parser: Unexpected end of file',
  TYPE_MISMATCH: "Parser: Expected type '{{expected}}'",
};

export const MODEL_ERROR_PATTERNS: ModelErrorPattern[] = [
  // ---Editor-level---
  {
    key: EDITOR_I18N_KEYS.REQUIRED_PROPERTY,
    regex: /Missing property "(.+)"\./,
    params: (match) => ({ property: match[1] }),
  },
  {
    key: EDITOR_I18N_KEYS.ADDITIONAL_PROPERTY,
    regex: /Property (.+) is not allowed\./,
    params: (match) => ({ property: match[1].replace(/"/g, '') }),
  },
  {
    key: EDITOR_I18N_KEYS.TYPE_MISMATCH,
    regex: /Incorrect type\. Expected "(.+)" but got "(.+)"\./,
    params: (match) => ({ expected: match[1], actual: match[2] }),
  },
  {
    key: EDITOR_I18N_KEYS.ENUM_MISMATCH,
    regex: /Value is not an accepted value\./,
    params: () => ({}),
  },
  {
    key: SCHEMA_I18N_KEYS.PROPERTY_NAME_CONTAINS_SPACE,
    regex: /String does not match the pattern of "\^\\S\+\$"\./,
    params: () => ({}),
  },
  {
    key: EDITOR_I18N_KEYS.PATTERN_MISMATCH,
    regex: /String does not match pattern "(.+)"\./,
    params: (match) => ({ pattern: match[1] }),
  },
  {
    key: EDITOR_I18N_KEYS.MIN_LENGTH,
    regex: /String is too short\. Minimum length is (\d+)\./,
    params: (match) => ({ min: match[1] }),
  },
  {
    key: EDITOR_I18N_KEYS.MAX_LENGTH,
    regex: /String is too long\. Maximum length is (\d+)\./,
    params: (match) => ({ max: match[1] }),
  },
  {
    key: EDITOR_I18N_KEYS.MIN_ITEMS,
    regex: /Array has too few items\. Minimum is (\d+)/,
    params: (match) => ({ min: match[1] }),
  },
  {
    key: EDITOR_I18N_KEYS.MAX_ITEMS,
    regex: /Array has too many items\. Maximum is (\d+)/,
    params: (match) => ({ max: match[1] }),
  },

  // ---Parser-level---
  {
    key: PARSER_I18N_KEYS.VALUE_EXPECTED,
    regex: /Value expected/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_JSON,
    regex: /Expected a JSON object, array or literal/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.DUPLICATE_KEY,
    regex: /Duplicate object key "(.+)"\./,
    params: (match) => ({ key: match[1] }),
  },
  {
    key: PARSER_I18N_KEYS.UNEXPECTED_TOKEN,
    regex: /Unexpected token (.+)/,
    params: (match) => ({ token: match[1].replace(/"|'/g, '') }),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_COLON,
    regex: /Expected ':' after property name/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_COMMA_OR_BRACKET,
    regex: /Expected comma or closing bracket/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.TRAILING_COMMA_NOT_ALLOWED,
    regex: /Trailing comma not allowed/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.UNCLOSED_STRING,
    regex: /String literal not closed/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_COMMA,
    regex: /Expected comma/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.PROPERTY_EXPECTED,
    regex: /Property expected/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.PROPERTY_KEY_QUOTES,
    regex: /Property keys must be doublequoted/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_VALUE_AFTER_COLON,
    regex: /Expected value after ':'/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_OBJECT_KEY,
    regex: /Expected property name/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.EXPECTED_OBJECT_OR_ARRAY,
    regex: /Expected object or array/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.INVALID_ESCAPE_CHARACTER,
    regex: /Invalid escape character/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.INVALID_NUMBER_FORMAT,
    regex: /Invalid number format/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.UNEXPECTED_END_OF_NUMBER,
    regex: /Unexpected end of number/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.UNEXPECTED_END_OF_STRING,
    regex: /Unexpected end of string/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.INVALID_UNICODE_SEQUENCE,
    regex: /Invalid Unicode escape sequence/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.UNEXPECTED_END_OF_COMMENT,
    regex: /Unexpected end of comment/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.COMMENT_NOT_ALLOWED,
    regex: /Comments are not permitted in JSON/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.TRAILING_COMMA,
    regex: /Trailing comma$/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.COLON_EXPECTED,
    regex: /Colon expected/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.DUPLICATE_PROPERTY,
    regex: /Duplicate object key/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.UNEXPECTED_END_OF_FILE,
    regex: /Unexpected end of file/,
    params: () => ({}),
  },
  {
    key: PARSER_I18N_KEYS.TYPE_MISMATCH,
    regex: /Incorrect type\. Expected one of (.+)\./,
    params: (match) => ({ expected: match[1] }),
  },
];
